import React, {useCallback, useEffect, useRef, useState} from 'react';

import {DeprecatedLogoHp, IconX} from '@veneer/core';

import {focusableElements} from '../../../../utils';
import {WFCButton} from '../../../WFCButton';
import {useAppSwitcherConfig} from '../../context/Config';
import {Config} from '../../utils/services/types';

import MenuItem from './components/MenuItem/MenuItem';
import SubMenu from './components/SubMenu/SubMenu';
import useStyles from './useStyles';
import {wexRedirects} from './utils';

const SubMenuControls = ({
    activeId,
    setSubMenuId,
}: {
    activeId: 'none' | keyof Config;
    setSubMenuId: React.Dispatch<React.SetStateAction<'none' | keyof Config>>;
}) => {
    const {serviceMeta, i18n} = useAppSwitcherConfig();
    const items = Object.keys(serviceMeta) as Array<keyof typeof serviceMeta>;

    return (
        <>
            {items.map(item => (
                <MenuItem
                    id={item}
                    aria-haspopup="menu"
                    key={item}
                    component="button"
                    active={item === activeId}
                    label={i18n.ariaLabels[item].toggle}
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={() => setSubMenuId(curr => (curr === item ? 'none' : item))}
                    icon={{name: 'IconHandWithSquare'}}
                    aria-label={i18n.ariaLabels[item].aria}
                />
            ))}
        </>
    );
};

const AppSwitcherMenu = ({
    open,
    onToggle,
    subMenu = false,
}: {
    open: boolean;
    onToggle: () => void;
    subMenu?: boolean;
}) => {
    const {wexDomains, environment, isHpEmail} = useAppSwitcherConfig();
    const styles = useStyles(open, subMenu);
    const {i18n, loginType} = useAppSwitcherConfig();

    const [subMenuId, setSubMenuId] = useState<'none' | keyof Config>('none');
    const wrapperRef = useRef<HTMLDivElement>(null);
    const isMounted = useRef(true);

    useEffect(() => {
        // Auto focus first element when opening side menu.
        const elements = wrapperRef.current?.querySelectorAll<HTMLElement>(focusableElements);
        elements?.[0].focus();
    }, [open]);

    useEffect(() => {
        isMounted.current = true;
        const element = wrapperRef.current;
        if (!element) {
            return;
        }
        const handleKeyDown = (e: KeyboardEvent) => {
            // Trap focus in the menu
            const elements = element.querySelectorAll<HTMLElement>(focusableElements);

            /* istanbul ignore else */
            if (elements.length !== 0) {
                // If reverse tabbing
                if (e.key === 'Tab' && e.shiftKey) {
                    //  focus last element if first has focus.
                    if (document.activeElement === elements[0]) {
                        elements[elements.length - 1].focus();
                        e.stopPropagation();
                        e.preventDefault();
                    }
                    return;
                }
                if (e.key === 'Tab') {
                    // if focus within side menu
                    /* istanbul ignore else */
                    if (element.contains(document.activeElement)) {
                        // and active elment is the last element, focus first.
                        if (document.activeElement === elements[elements.length - 1]) {
                            e.preventDefault();
                            elements[0]?.focus();
                        }
                    }
                }
            }

            // Close menu if Escape key pressed.
            if (e.key === 'Escape' && open) {
                if (subMenuId !== 'none') {
                    document.querySelector<HTMLButtonElement>(`button#${subMenuId}`)?.focus();
                    // eslint-disable-next-line i18next/no-literal-string
                    setSubMenuId('none');
                    return;
                }
                onToggle();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            isMounted.current = false;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onToggle, open, subMenuId]);

    const handleClose = useCallback(
        (e: React.MouseEvent | React.SyntheticEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            // eslint-disable-next-line i18next/no-literal-string
            setSubMenuId('none');
            onToggle();
        },
        [onToggle]
    );

    const handleCloseSubNav = useCallback(() => {
        const btn = document.querySelector<HTMLButtonElement>(`button#${subMenuId}`);
        btn?.focus();

        // eslint-disable-next-line i18next/no-literal-string
        setSubMenuId('none');
    }, [subMenuId]);

    if (!open) {
        return <></>;
    }

    const sanitizeUrl = (url: string) => {
        // Clean any accidentally added doubles slashes. //
        return url.replaceAll(/(?<!:)\/\//g, '/');
    };

    // Replace double slashes.
    const homeUrl = sanitizeUrl(`${wexDomains[environment]}${wexRedirects[isHpEmail ? 'AAD' : loginType]}`);

    return (
        <>
            {/* We don't care about making the backdrop focusable for keyboards. Doesn't make sense, they can use the button.*/}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <div
                data-testid={'AppSwitcher-backdrop'}
                className={styles.backdrop}
                onClick={handleClose}
            />
            <div
                ref={wrapperRef}
                data-testid={'AppSwitcher--menu--level-1'}
            >
                <div className={styles.wrapper}>
                    <div className={styles.heading}>
                        <WFCButton
                            aria-label={i18n.closeAppSwitcher}
                            onClick={handleClose}
                            leadingIcon={<IconX aria-hidden />}
                        />
                        <DeprecatedLogoHp
                            size={24}
                            color="white"
                        />
                    </div>
                    <div className={styles.inner}>
                        <nav
                            className={styles.nav}
                            aria-label={i18n.appSwicherMenu}
                        >
                            <MenuItem
                                component="anchor"
                                label={i18n.home}
                                aria-label={i18n.home}
                                href={homeUrl}
                                icon={{name: 'IconHome'}}
                            />

                            <SubMenuControls
                                activeId={subMenuId}
                                setSubMenuId={setSubMenuId}
                            />
                        </nav>

                        <nav
                            className={styles.companyNav}
                            aria-label={i18n.myCompany}
                        >
                            <p className="overline-regular">{i18n.myCompany}</p>

                            <MenuItem
                                component="anchor"
                                label={i18n.usersAndRoles}
                                href={sanitizeUrl(wexDomains[environment] + '/ui/adminx/users')}
                                icon={{name: 'IconPeople'}}
                            />

                            <MenuItem
                                component="anchor"
                                label={i18n.settings}
                                href={sanitizeUrl(wexDomains[environment] + '/ui/adminx/settings')}
                                icon={{name: 'IconGear'}}
                            />

                            <MenuItem
                                component="anchor"
                                label={i18n.helpAndSupport}
                                href={sanitizeUrl(wexDomains[environment] + '/ui/adminx/support')}
                                icon={{name: 'IconGear'}}
                            />
                        </nav>
                    </div>
                </div>
                <div>
                    {subMenuId !== 'none' && (
                        <SubMenu
                            onClose={handleCloseSubNav}
                            id={subMenuId}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default AppSwitcherMenu;
